const canonicalDateOnly=/^(\d{4})-(\d{2})-(\d{2})$/;
const displayDateOnly=/^(\d{2})\/(\d{2})\/(\d{4})$/;

function isRealDate(year:number,month:number,day:number){
  if(year<1||month<1||month>12||day<1)return false;
  const leap=year%4===0&&(year%100!==0||year%400===0);
  const days=[31,leap?29:28,31,30,31,30,31,31,30,31,30,31];
  return day<=days[month-1]!;
}

export function dateOnlyInputValue(value:string|null|undefined){
  if(!value)return'';
  const match=canonicalDateOnly.exec(value);
  if(!match)return'';
  const[,year,month,day]=match;
  return isRealDate(Number(year),Number(month),Number(day))?`${day}/${month}/${year}`:'';
}

export function parseDateOnlyInput(value:string):string|null{
  const trimmed=value.trim();
  if(!trimmed)return null;
  const match=displayDateOnly.exec(trimmed);
  if(!match)throw new Error('Enter a real date as DD/MM/YYYY');
  const[,day,month,year]=match;
  if(!isRealDate(Number(year),Number(month),Number(day)))
    throw new Error('Enter a real date as DD/MM/YYYY');
  return`${year}-${month}-${day}`;
}

export function formatDateOnly(value:string|null|undefined,fallback='Unknown'){
  return dateOnlyInputValue(value)||fallback;
}

function ukTimestampParts(value:string|Date|null|undefined){
  if(!value)return null;
  const date=value instanceof Date?value:new Date(value);
  if(Number.isNaN(date.getTime()))return null;
  return Object.fromEntries(new Intl.DateTimeFormat('en-GB',{
    day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit',hourCycle:'h23',
    timeZone:'Europe/London',
  }).formatToParts(date).map(part=>[part.type,part.value]));
}

export function formatTimestampDate(value:string|Date|null|undefined,fallback='Unknown'){
  const canonical=timestampDateOnlyValue(value);
  return canonical?dateOnlyInputValue(canonical):fallback;
}

export function timestampDateOnlyValue(value:string|Date|null|undefined){
  const parts=ukTimestampParts(value);
  return parts?`${parts.year}-${parts.month}-${parts.day}`:'';
}

export function formatDateTime(value:string|Date|null|undefined,fallback='Unknown'){
  const parts=ukTimestampParts(value);
  if(!parts)return fallback;
  return`${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
}

/** Availability intervals retain their recorded zone. Include the UTC offset so
 * two occurrences of the same clock time remain distinguishable on a fold. */
export function formatZonedDateTime(value:string|Date,timeZone:string){
  const date=value instanceof Date?value:new Date(value);
  if(Number.isNaN(date.getTime()))return 'Unknown';
  const parts=Object.fromEntries(new Intl.DateTimeFormat('en-GB',{timeZone,day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit',hourCycle:'h23',timeZoneName:'shortOffset'}).formatToParts(date).map(p=>[p.type,p.value]));
  return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute} ${parts.timeZoneName?.replace(/^GMT[+-]0$/,'GMT')}`;
}

export function dateTimeLocalInputValue(value:string|Date|null|undefined){
  const parts=ukTimestampParts(value);
  if(!parts)return'';
  return`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export function localDateOnlyValue(date=new Date()){
  const year=String(date.getFullYear()).padStart(4,'0');
  const month=String(date.getMonth()+1).padStart(2,'0');
  const day=String(date.getDate()).padStart(2,'0');
  return`${year}-${month}-${day}`;
}

export function formatDateOnlyValues(value:unknown):string{
  if(value===null||value===undefined)return'Unknown';
  if(typeof value==='string')return dateOnlyInputValue(value)||value;
  if(typeof value==='object')return JSON.stringify(value,(_key,nested)=>
    typeof nested==='string'?(dateOnlyInputValue(nested)||nested):nested,
  );
  return String(value);
}


/** Match the London display clock and retain an unchanged recorded instant, including seconds. */
export function londonDateTimeInputInstant(value:string,original?:string|null):string|undefined{
 if(!value)return undefined;
 if(original&&dateTimeLocalInputValue(original)===value)return new Date(original).toISOString();
 if(!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value))throw Error('Enter a valid London date and time.');
 const wall=Date.parse(value+'Z');
 // Modern Europe/London uses GMT or BST. Round-trip both offsets instead of guessing at clock changes.
 const matches=[0,60].map(offset=>new Date(wall-offset*60000)).filter(date=>Number.isFinite(date.getTime())&&dateTimeLocalInputValue(date)===value);
 if(matches.length!==1)throw Error('This London time is missing or occurs twice when the clocks change. Choose an unambiguous time.');
 return matches[0]!.toISOString();
}

export function procurementDeadline(value:string,original?:string):string|undefined{
 return londonDateTimeInputInstant(value,original);
}

/** A recurring year boundary has no particular calendar year. */
export function monthName(month:number){return Number.isInteger(month)&&month>=1&&month<=12?new Intl.DateTimeFormat('en-GB',{month:'long',timeZone:'UTC'}).format(new Date(Date.UTC(2001,month-1,1))):'Unknown month';}
export function formatRecurringDate(month:number,day:number){return isRealDate(2001,month,day)?`${day} ${monthName(month)}`:'Unknown date';}

/** Readable due dates without coercing absent or invalid dates into today. */
export function formatDueDate(value:string|null|undefined,now=new Date(),fallback='Unknown'){
 if(!dateOnlyInputValue(value))return fallback;
 const year=timestampDateOnlyValue(now).slice(0,4);
 const date=new Date(value+'T12:00:00Z');
 return new Intl.DateTimeFormat('en-GB',{day:'numeric',month:'short',...(value!.slice(0,4)!==year?{year:'numeric' as const}:{}),timeZone:'Europe/London'}).format(date);
}
export function isPastDue(value:string|null|undefined,now=new Date()){
 return Boolean(dateOnlyInputValue(value)&&value!<timestampDateOnlyValue(now));
}
export function formatActivityTime(value:string|Date|null|undefined,now=new Date()){
 const day=timestampDateOnlyValue(value);if(!day)return 'Unknown';
 const today=timestampDateOnlyValue(now);
 const previous=new Date(today+'T12:00:00Z');previous.setUTCDate(previous.getUTCDate()-1);const yesterday=previous.toISOString().slice(0,10);
 const parts=ukTimestampParts(value)!;
 return `${day===today?'Today':day===yesterday?'Yesterday':formatDueDate(day,now)} ${parts.hour}:${parts.minute}`;
}

/** Hour used by the London calendar grid; invalid source instants remain unknown. */
export function londonHour(value:string|Date|null|undefined):number|null{
 if(!value)return null;const date=value instanceof Date?value:new Date(value);
 return Number.isFinite(date.getTime())?Number(new Intl.DateTimeFormat('en-GB',{timeZone:'Europe/London',hour:'2-digit',hourCycle:'h23'}).format(date)):null;
}
